import React, { useState, useEffect } from 'react';
import { 
  Calendar, MapPin, Clock, ArrowRight, Search,
  Loader2, Users, Globe, DollarSign, Filter
} from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ['Todos', 'Capacitación', 'Networking', 'Feria', 'Webinar', 'Taller', 'Conferencia'];

const EventosPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [showPast, setShowPast] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchEvents();
  }, [selectedCategory]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const params = selectedCategory !== 'Todos' ? `?category=${selectedCategory}` : '';
      const response = await axios.get(`${API_URL}/api/events/public${params}`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('es-PY', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  const isPast = (dateStr) => dateStr ? new Date(dateStr) < new Date() : false;

  const filteredEvents = events.filter(e => {
    const matchesSearch = e.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         e.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPast = showPast || !isPast(e.event_date);
    return matchesSearch && matchesPast;
  });

  const upcomingEvents = filteredEvents.filter(e => !isPast(e.event_date));
  const pastEvents = filteredEvents.filter(e => isPast(e.event_date));

  return (
    <div className="min-h-screen bg-white" data-testid="eventos-page">
      <Navigation />
      
      {/* Hero */}
      <section className="relative pt-32 pb-12 bg-gradient-to-br from-zinc-900 via-emerald-900 to-zinc-900 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-20 w-72 h-72 bg-emerald-500/20 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex items-center gap-2 text-emerald-400 mb-6">
            <Calendar className="w-5 h-5" />
            <span className="text-sm font-medium tracking-wider uppercase">Eventos</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Eventos y<span className="block text-emerald-400">Capacitaciones</span>
          </h1>
          <p className="text-xl text-zinc-300 max-w-2xl">
            Participá en nuestros eventos, talleres y capacitaciones sobre energía solar y sostenibilidad.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-white border-b sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative w-full md:w-96">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
              <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar eventos..." className="w-full pl-12 pr-4 py-3 border rounded-xl" />
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              {CATEGORIES.map((cat) => (
                <button key={cat} onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === cat ? 'bg-emerald-600 text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                  }`}>{cat}</button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Events */}
      <section className="py-16 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-20">
              <Calendar className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-zinc-900 mb-2">No hay eventos disponibles</h3>
              <p className="text-zinc-500">Pronto anunciaremos nuevos eventos. ¡Volvé a visitarnos!</p>
            </div>
          ) : (
            <>
              {/* Upcoming Events */}
              {upcomingEvents.length > 0 && (
                <div className="mb-12">
                  <h2 className="text-2xl font-bold text-zinc-900 mb-6">Próximos Eventos</h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {upcomingEvents.map((event) => (
                      <Link key={event.id} to={`/eventos/${event.slug}`}
                        className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all border">
                        {event.image_url && (
                          <div className="h-48 overflow-hidden">
                            <img src={event.image_url} alt={event.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                          </div>
                        )}
                        <div className="p-5">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">{event.category}</span>
                            {event.is_online && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded flex items-center gap-1"><Globe className="w-3 h-3" />Online</span>}
                            {event.is_free && <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">Gratis</span>}
                          </div>
                          <h3 className="font-bold text-zinc-900 mb-2 group-hover:text-emerald-600 transition-colors line-clamp-2">{event.title}</h3>
                          <p className="text-sm text-zinc-500 mb-3 line-clamp-2">{event.description}</p>
                          <div className="flex flex-col gap-1 text-xs text-zinc-400">
                            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(event.event_date)}{event.event_time && ` - ${event.event_time}`}</span>
                            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{event.location}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Past Events Toggle */}
              {pastEvents.length > 0 && (
                <div>
                  <button onClick={() => setShowPast(!showPast)}
                    className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 mb-6">
                    <Filter className="w-5 h-5" />
                    {showPast ? 'Ocultar eventos pasados' : `Ver ${pastEvents.length} eventos pasados`}
                  </button>
                  
                  {showPast && (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 opacity-70">
                      {pastEvents.map((event) => (
                        <div key={event.id} className="bg-white rounded-xl p-5 border">
                          <span className="px-2 py-0.5 bg-zinc-100 text-zinc-500 text-xs rounded mb-2 inline-block">Finalizado</span>
                          <h3 className="font-semibold text-zinc-700 mb-2">{event.title}</h3>
                          <p className="text-sm text-zinc-400">{formatDate(event.event_date)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-emerald-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">¿Querés organizar un evento con nosotros?</h2>
          <p className="text-emerald-100 mb-8">Contactanos para charlas, talleres o capacitaciones sobre energía solar.</p>
          <a href="https://wa.me/595991344956?text=Hola,%20me%20interesa%20organizar%20un%20evento" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-emerald-600 font-semibold rounded-xl hover:bg-emerald-50 transition-all">
            Contactar <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default EventosPage;
